'use client';

import { c as _c } from "react/compiler-runtime";
import { z } from 'zod';
import { useTRPCClient } from '~/modules/trpc/client';
import { useMountEffect } from '~/utils/react';
import { GetActivityQuoteInputSchema, SearchActivitiesInputSchema, WEBMCP_TOOL_NAMES } from './schemas';
import { registerWebMcpTools, type ModelContext, type ModelContextTool } from './registerWebMcpTools';
function findModelContext(): ModelContext | null {
  if (typeof document === 'undefined') return null;
  const fromDocument = (document as {
    modelContext?: ModelContext;
  }).modelContext;
  const fromNavigator = (navigator as {
    modelContext?: ModelContext;
  }).modelContext;
  const ctx = fromDocument ?? fromNavigator;
  return ctx && typeof ctx.registerTool === 'function' ? ctx : null;
}

/**
 * Registers the discovery tools with the browser's WebMCP model context so an
 * agent driving the page can search activities and ask for an exact quote. A
 * no-op in browsers without the API. Renders nothing.
 */
export function WebMcpTools(props) {
  const $ = _c(3);
  const trpc = useTRPCClient();
  let t0;
  if ($[0] !== props.locale || $[1] !== trpc) {
    t0 = () => {
      const ctx = findModelContext();
      if (!ctx) {
        return;
      }
      const tools = [{
        name: WEBMCP_TOOL_NAMES.search,
        description: "Search the Funkey team building catalogue. Returns matching activities with the price per participant for the given party size, VAT included. Read-only.",
        inputSchema: z.toJSONSchema(SearchActivitiesInputSchema),
        annotations: {
          readOnlyHint: true
        },
        execute: input => trpc.webmcp.searchActivities.query({
          ...SearchActivitiesInputSchema.parse(input),
          locale: props.locale
        })
      }, {
        name: WEBMCP_TOOL_NAMES.quote,
        description: "Exact price of one activity for a number of participants, per variant, VAT included. Also lists where it takes place and, when a departure place is given, the distance to the nearest venue and an estimated transport cost. Read-only.",
        inputSchema: z.toJSONSchema(GetActivityQuoteInputSchema),
        annotations: {
          readOnlyHint: true
        },
        execute: input_0 => trpc.webmcp.getActivityQuote.query({
          ...GetActivityQuoteInputSchema.parse(input_0),
          locale: props.locale
        })
      }];
      return registerWebMcpTools(ctx, tools);
    };
    $[0] = props.locale;
    $[1] = trpc;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  useMountEffect(t0);
  return null;
}